import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import composeRequestArgs from "./composeRequestArgs";
import { fetchData, FetchDataArgs } from "../utils/fetchData";
import formatPaginationData from "../components/Pagination/formatPaginationData";
import formatSortOrder from "../components/SearchResultsTable/formatSortOrder";
import getPageOffset from "../components/Pagination/getPageOffset";
import {
    DataProviderProps,
    SearchFilters,
    SearchQuestionContextProps,
    SearchResultsContextProps,
    SortValues,
} from "../types/context";
import { FunnelbackResponse } from "../types/funnelback";
import composeInitialSort from "./composeInitialSort";
import resolveFacets from "./resolveFacets";

const SearchResultsContext = createContext<
    SearchResultsContextProps | Record<string, never>
>({});
const SearchQuestionContext = createContext<
    SearchQuestionContextProps | Record<string, never>
>({});

export const showAllQuery = "!showall";

const DataProvider = ({
    children,
    config,
    maxPages = 5,
}: DataProviderProps) => {
    const [searchParams] = useSearchParams();
    const initialQuery = searchParams.get("query")?.trim() ?? showAllQuery;

    const [response, setResponse] = useState<
        FunnelbackResponse | Record<string, never>
    >({});
    const [defaultResponse, setDefaultResponse] = useState<
        FunnelbackResponse | Record<string, never>
    >({});
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [searchFilters, setSearchFilters] = useState<SearchFilters>({});

    const results = response?.response?.resultPacket?.results || [];
    const facets =
        resolveFacets(response?.response?.facets, config.collection) || [];
    const allFacets =
        resolveFacets(defaultResponse?.response?.facets, config.collection) ||
        [];

    const resultsSummary =
        response?.response?.resultPacket?.resultsSummary || {};
    const pagination = formatPaginationData(resultsSummary, maxPages);
    const sortOrder = formatSortOrder(
        response?.question?.rawInputParameters?.sort
            ? response?.question?.rawInputParameters?.sort[0]
            : ""
    );

    const getData = (args?: Omit<FetchDataArgs, "config">) =>
        fetchData({ ...args, config }).then(setResponse);

    // Set initial response on component mount
    useEffect(() => {
        if (!config?.endpoint || !config?.collection) return;
        const initialQuery = composeRequestArgs(searchParams);
        if (config?.initialSort) {
            initialQuery.sort = composeInitialSort(config.initialSort);
        }

        fetchData({ config }).then(setDefaultResponse);
        getData(initialQuery);
    }, []);

    // Set query helper
    function setQuery(value: string) {
        const queryValue = value.trim() ? value.trim() : showAllQuery;
        setSearchQuery(queryValue);
        getData({ query: value, params: searchFilters });
    }

    // Set filter helper
    function setSingleFilter(filter: string, value: string) {
        const filters = {
            ...searchFilters,
            [filter]: value,
        };
        setMultipleFilters(filters);
    }

    // Set multiple facet values
    function setMultipleFilters(filters: SearchFilters) {
        setSearchFilters(filters);
        getData({ query: searchQuery, params: filters });
    }

    function setFormQuestion(formQuery: string, filters: SearchFilters) {
        const queryValue = formQuery.trim() ? formQuery.trim() : showAllQuery;
        setSearchQuery(queryValue);
        setSearchFilters(filters);
        getData({ query: queryValue, params: filters });
    }

    // Clear filters helper
    function clearFilters() {
        setSearchFilters({});
        getData({ query: searchQuery });
    }

    // Set pagination
    function setPage(pageNumber: number) {
        // calculate page from offset
        const limit =
            resultsSummary && resultsSummary.numRanks
                ? resultsSummary.numRanks
                : 10;
        const offset = getPageOffset(pageNumber, limit);
        getData({
            query: searchQuery,
            params: searchFilters,
            offset,
            sort: sortOrder,
        });
    }

    function setSortOrder(field: string, direction: SortValues) {
        getData({
            query: searchQuery,
            params: searchFilters,
            sort: { field, direction },
        });
    }

    const searchResultsContextValue = {
        config,
        facets,
        allFacets,
        pagination,
        query: response as FunnelbackResponse,
        results,
        resultsSummary,
    };
    const searchQuestionContextValue = {
        filters: searchFilters,
        query: searchQuery,
        sortOrder,
        clearFilters,
        setSingleFilter,
        setMultipleFilters,
        setFormQuestion,
        setPage,
        setQuery,
        setSortOrder,
    };

    return (
        <SearchResultsContext.Provider value={searchResultsContextValue}>
            <SearchQuestionContext.Provider value={searchQuestionContextValue}>
                {children}
            </SearchQuestionContext.Provider>
        </SearchResultsContext.Provider>
    );
};

export const useSearchResults = () => {
    return useContext(SearchResultsContext) as SearchResultsContextProps;
};

export const useSearchQuestion = () => {
    return useContext(SearchQuestionContext) as SearchQuestionContextProps;
};

export default DataProvider;
